import Bugsnag from '@bugsnag/js'
import metamask from '@/util/metamask'
import IERC20 from '@/abi/IERC20.json'
import { wasNotDeclined } from '@/util/Errors'
import { txFees } from '@/util/txFees'
import IGatewayModule from '@/abi/IGatewayModule.json'

export class TransactionDeclined extends Error {
	constructor() {
		super('User declined transaction')
	}
}

export class TransactionFailed extends Error {
	cause?: unknown

	// cause is kept for logging only; users see the generic message
	constructor(cause?: unknown) {
		super('Transaction failed unexpectedly')
		this.cause = cause
	}
}

// tx should capture from/chain at send time; metamask.state can change while a send is pending
function reportFailure(context: string, err: any, tx: object) {
	console.error(`${context} transaction failed`, { error: err, tx })
	Bugsnag.notify(err, (event) => {
		event.context = context
		event.addMetadata('transaction', tx)
	})
}

// dev-only: full payload so reverts can be reproduced (e.g. cast call / tenderly)
function debugTx(context: string, tx: any, params: object) {
	if (import.meta.env.MODE !== 'development') {
		return
	}

	let data: string | undefined
	try {
		data = tx.encodeABI()
	} catch (e) {
		data = undefined
	}

	console.log(`${context} tx payload`, {
		to: tx._parent?.options?.address,
		method: tx._method?.name,
		args: tx.arguments,
		data,
		...params,
	})
}

export interface AuthedTransaction {
	network_id: number | string
	safe: string
	gateway: string
	destination: string
	transaction: string
	expiration: string
	identifier: string
	counter: string
	salt: string
	signature: string
}

async function requestChain(chainId): Promise<boolean> {
	return await metamask.requestChain(chainId).then(async (res) => {
		return res === true
	})
}

class Transaction {
	network: number
	contract: string

	constructor(network: number, contract: string) {
		this.network = network
		this.contract = contract
	}

	async assertChain() {
		if (metamask.state.chain !== this.network) {
			let swap = await requestChain(this.network)

			if (false == swap) {
				throw new Error('Failed to switch to the correct chain to execute transaction.')
			}
		}
	}
}

class Deposit extends Transaction {
	async execute(to: string, amt: BigInt): Promise<any> {
		await this.assertChain()

		const tx = await this.buildTx(to, amt)
		const report = {
			network: this.network,
			contract: this.contract,
			to,
			amount: amt.toString(),
			from: metamask.state.wallet,
			chain: metamask.state.chain,
		}
		debugTx('exchange:deposit', tx, { from: metamask.state.wallet, chain: metamask.state.chain })

		let gasEstimate
		try {
			gasEstimate = await tx.estimateGas({
				from: metamask.state.wallet,
				gas: 500_000,
			})
		} catch (e) {
			console.error('failed to estimate gas', { error: e, metamask: metamask.state })
			throw new Error('Failed to deposit, gas estimation failed.')
		}

		gasEstimate = Math.ceil(gasEstimate * 1.3)

		const fees = await txFees(metamask.ethereum)
		debugTx('exchange:deposit', tx, { gas: gasEstimate, ...fees })

		let response = new Promise(async (resolve, reject) => {
			let confirmed = false
			await tx
				.send({
					from: metamask.state.wallet,
					gas: gasEstimate,
					...fees,
				})
				.on('receipt', async (r) => {
					// receipt fires when receipt is available
					// not when tx is confirmed
					// resolve(r)
				})
				.on('confirmation', (c, receipt) => {
					if (c == 1) {
						confirmed = true
						console.log('receipt', receipt)
						resolve(receipt)
					}
				})
				.catch((err: Error) => {
					if (wasNotDeclined(err)) {
						reportFailure('exchange:deposit', err, report)
						reject(new TransactionFailed(err))
						return
					}

					reject(new TransactionDeclined())
				})
		})

		return await response
	}

	async buildTx(to: string, amt: BigInt): Promise<any> {
		let contract = await metamask.loadContract(IERC20, this.contract)
		const tx = await contract.methods.transfer(to, amt.toString())

		return tx
	}
}

class Withdraw extends Transaction {
	async execute(authed: AuthedTransaction): Promise<any> {
		await this.assertChain()

		const tx = await this.buildTx(authed)
		const from = metamask.state.wallet
		const report = {
			network: this.network,
			gateway: this.contract,
			destination: authed.destination,
			identifier: authed.identifier,
			counter: authed.counter,
			expiration: authed.expiration,
			from,
			chain: metamask.state.chain,
		}
		debugTx('exchange:withdraw', tx, { from, chain: metamask.state.chain })

		let gasEstimate: any
		try {
			gasEstimate = await tx.estimateGas({
				from,
				gas: 500_000,
			})
		} catch (e) {
			reportFailure('exchange:withdraw', e, report)
			throw new Error('Failed to withdraw, gas estimation failed.', { cause: e })
		}

		gasEstimate = Math.ceil(gasEstimate * 1.3)

		// the wallet can switch networks while gas is being estimated
		if (metamask.state.chain !== this.network) {
			throw new Error('Wallet network changed before sending. Please try again.')
		}

		const fees = await txFees(metamask.ethereum)
		debugTx('exchange:withdraw', tx, { from, gas: gasEstimate, ...fees })

		let response = new Promise(async (resolve, reject) => {
			let confirmed = false
			await tx
				.send({
					from,
					gas: gasEstimate,
					...fees,
				})
				.on('receipt', async (r) => {
					// receipt fires when receipt is available
					// not when the tx is confirmed
					// resolve(r)
				})
				.on('confirmation', (c, receipt) => {
					if (c == 1) {
						confirmed = true
						console.log('receipt', receipt)
						resolve(receipt)
					}
				})
				.catch((err: Error) => {
					if (wasNotDeclined(err)) {
						reportFailure('exchange:withdraw', err, report)
						reject(new TransactionFailed(err))
						return
					}

					reject(new TransactionDeclined())
				})
		})

		return await response
	}

	async buildTx(authed: AuthedTransaction): Promise<any> {
		let contract = await metamask.loadContract(IGatewayModule, this.contract)

		const tx = await contract.methods.execute(
			authed.safe,
			authed.destination,
			authed.transaction,
			false,
			authed.expiration,
			authed.identifier,
			authed.counter,
			authed.salt,
			authed.signature,
		)

		return tx
	}
}

async function deposit(network: number, contract: string, to: string, amt: BigInt): Promise<any> {
	let deposit = new Deposit(network, contract)
	let response = await deposit.execute(to, amt)

	return response
}

async function withdraw(authed: AuthedTransaction): Promise<any> {
	let withdraw = new Withdraw(parseInt(String(authed.network_id)), authed.gateway)
	let response = await withdraw.execute(authed)

	return response
}

export default {
	deposit,
	withdraw,
	errors: {
		TransactionDeclined,
		TransactionFailed,
	},
}
